import { useState } from "react";
import { LayoutTemplate, Pencil, Trash2, Send, Eye, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "@/hooks/useApi";
import { listEmbeds, saveEmbed, deleteEmbed, previewEmbed, sendEmbed, sendStoredEmbed } from "@/api/embeds";
import { CrudCard, ListRow, IconButton } from "./CrudCard";
import { Modal } from "./Modal";
import { showApiError } from "./helpers";
import { Field, TextInput, ColorInput, ChannelSelect, RoleSelect, ToggleRow, Toggle } from "./fields";
import { ChannelSendCheck } from "./ChannelSendCheck";

const URL_FIELDS = ["url", "author_url", "author_icon", "thumbnail", "image", "footer_icon"];
const URL_LABELS = { url: "URL", author_url: "Author URL", author_icon: "Author icon", thumbnail: "Thumbnail", image: "Image", footer_icon: "Footer icon" };

export const validateUrl = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const v = String(value).trim();
  if (/^(javascript|data|file):/i.test(v)) return "unsafe scheme";
  if (/localhost|127\.0\.0\.1|0\.0\.0\.0/i.test(v)) return "local URLs not allowed";
  try {
    const u = new URL(v);
    if (!["http:", "https:"].includes(u.protocol)) return "must start with http(s)://";
    return null;
  } catch {
    return "invalid URL";
  }
};

const EMPTY_FORM = {
  type: "",
  enabled: true,
  channel_id: null,
  content: "",
  title: "",
  description: "",
  url: "",
  timestamp: false,
  embed_color: "#FFFFFF",
  author_name: "",
  author_url: "",
  author_icon: "",
  thumbnail: "",
  image: "",
  footer: "",
  footer_icon: "",
  mention_role: null,
  fields: [],
};

const toEmbedPayload = (f) => ({
  title: f.title || null,
  description: f.description || null,
  url: f.url?.trim() || null,
  color: f.embed_color || null,
  timestamp: !!f.timestamp,
  thumbnail: f.thumbnail?.trim() || null,
  image: f.image?.trim() || null,
  author: f.author_name || f.author_url || f.author_icon
    ? { name: f.author_name || null, url: f.author_url?.trim() || null, icon_url: f.author_icon?.trim() || null }
    : null,
  footer: f.footer || f.footer_icon
    ? { text: f.footer || null, icon_url: f.footer_icon?.trim() || null }
    : null,
  fields: (f.fields || [])
    .filter((x) => x.name.trim() && x.value.trim())
    .map((x) => ({ name: x.name.trim(), value: x.value.trim(), inline: !!x.inline })),
});

const EmbedPreview = ({ form }) => (
  <div className="overflow-hidden rounded-xl border border-white/10 bg-ink-800" data-testid="embed-preview">
    <div
      className="border-l-2 bg-white/[0.02] p-4"
      style={{ borderLeftColor: /^#[0-9a-fA-F]{6}$/.test(form.embed_color || "") ? form.embed_color : "#FFFFFF" }}
    >
      {form.author_name && (
        <p className="mb-1.5 flex items-center gap-2 text-xs font-semibold text-paper-200">
          {form.author_icon && validateUrl(form.author_icon) === null && (
            <img src={form.author_icon} alt="" className="h-5 w-5 rounded-full object-cover" />
          )}
          {form.author_name}
        </p>
      )}
      {form.title && <p className="mb-1 text-sm font-bold text-paper-100">{form.title}</p>}
      {form.description && <p className="whitespace-pre-wrap text-sm text-paper-300">{form.description}</p>}
      {(form.fields || []).filter((x) => x.name.trim()).length > 0 && (
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {form.fields.filter((x) => x.name.trim()).map((x, i) => (
            <div key={i} className={x.inline ? "" : "sm:col-span-2"}>
              <p className="text-xs font-bold text-paper-100">{x.name}</p>
              <p className="text-xs text-paper-400">{x.value}</p>
            </div>
          ))}
        </div>
      )}
      {form.image && validateUrl(form.image) === null && (
        <img src={form.image} alt="" className="mt-3 max-h-40 rounded-lg object-cover" />
      )}
      <div className="mt-3 flex items-center gap-2">
        {form.thumbnail && validateUrl(form.thumbnail) === null && <span className="font-mono text-[10px] text-paper-500">[thumbnail set]</span>}
        {form.footer && <p className="text-[11px] text-paper-500">{form.footer}</p>}
        {form.timestamp && <p className="font-mono text-[10px] text-paper-500">· now</p>}
      </div>
    </div>
    {form.content && <p className="border-t border-white/5 px-4 py-2 text-xs text-paper-400">{form.content}</p>}
  </div>
);

const FieldsEditor = ({ fields, onChange }) => (
  <div className="space-y-3 rounded-xl border border-white/5 bg-white/[0.02] p-4">
    <div className="flex items-center justify-between">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-paper-500">Fields ({fields.length}/25)</p>
      <button
        onClick={() => fields.length < 25 && onChange([...fields, { name: "", value: "", inline: true }])}
        disabled={fields.length >= 25}
        data-testid="embed-add-field"
        className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold text-paper-200 transition-colors duration-300 hover:bg-white/10 disabled:opacity-40"
      >
        <Plus className="h-3 w-3" aria-hidden="true" />
        Add field
      </button>
    </div>
    {fields.map((field, i) => (
      <div key={i} className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[1fr_1fr_auto_auto]">
        <TextInput value={field.name} onChange={(v) => onChange(fields.map((x, j) => (j === i ? { ...x, name: v } : x)))} placeholder="Name" testId={`embed-field-name-${i}`} />
        <TextInput value={field.value} onChange={(v) => onChange(fields.map((x, j) => (j === i ? { ...x, value: v } : x)))} placeholder="Value" testId={`embed-field-value-${i}`} />
        <Toggle checked={field.inline} onChange={(v) => onChange(fields.map((x, j) => (j === i ? { ...x, inline: v } : x)))} label="Inline" testId={`embed-field-inline-${i}`} />
        <button onClick={() => onChange(fields.filter((_, j) => j !== i))} aria-label="Remove field" data-testid={`embed-field-remove-${i}`} className="text-paper-500 transition-colors hover:text-paper-100">
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    ))}
  </div>
);

const SendTemplateModal = ({ guildId, item, channels, onClose }) => {
  const [channelId, setChannelId] = useState(item.channel_id || "");
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!channelId) {
      toast.error("Pick a channel first.");
      return;
    }
    setSending(true);
    try {
      const res = await sendStoredEmbed(guildId, item.type, { channel_id: channelId });
      toast.success(
        <span>
          Sent to the server.{" "}
          {res.message_url && (
            <a href={res.message_url} target="_blank" rel="noopener noreferrer" className="underline">
              View message
            </a>
          )}
        </span>
      );
      onClose();
    } catch (e) {
      showApiError(e);
    } finally {
      setSending(false);
    }
  };
  return (
    <Modal open onClose={onClose} title={`Send "${item.type}"`} testId="embed-send-template-modal">
      <Field label="Channel">
        <ChannelSelect value={channelId} onChange={setChannelId} channels={channels} types={["text", "news"]} testId="embed-send-channel" />
      </Field>
      <ChannelSendCheck guildId={guildId} channelId={channelId} />
      <button
        onClick={send}
        disabled={sending || !channelId}
        data-testid="embed-send-template-submit"
        className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-white py-3 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.01] disabled:opacity-50"
      >
        <Send className="h-4 w-4" aria-hidden="true" />
        {sending ? "Sending…" : "Send embed"}
      </button>
    </Modal>
  );
};

export const EmbedBuilder = ({ guildId, channels, roles }) => {
  const { data, loading, error, refetch } = useApi(() => listEmbeds(guildId));
  const [modal, setModal] = useState(null); // { isNew, form }
  const [sendTarget, setSendTarget] = useState(null); // template row
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy] = useState(null); // 'save' | 'send' | 'preview'

  const items = (data?.embeds || []).filter((e) => e.type && !["welcome", "goodbye"].includes(e.type));
  const channelName = (id) => {
    const ch = channels.find((c) => String(c.id) === String(id));
    return ch ? `#${ch.name}` : null;
  };
  const selectableRoles = roles.filter((r) => !r.managed && r.name !== "@everyone");

  const openNew = () => {
    setFieldErrors({});
    setModal({ isNew: true, form: { ...EMPTY_FORM, fields: [] } });
  };
  const openEdit = (item) => {
    setFieldErrors({});
    setModal({
      isNew: false,
      form: {
        ...EMPTY_FORM,
        ...item,
        content: item.message || "",
        footer: item.footer || "",
        fields: [],
      },
    });
  };

  const validate = (form) => {
    const errs = {};
    if (modal.isNew && !/^[a-z0-9_-]{2,32}$/i.test(form.type.trim())) errs.type = "2–32 letters, numbers, - or _";
    URL_FIELDS.forEach((f) => {
      const err = validateUrl(form[f]);
      if (err) errs[f] = `${URL_LABELS[f]} is invalid.`;
    });
    if ((form.title || "").length > 256) errs.title = "Title exceeds 256 characters.";
    if ((form.description || "").length > 4096) errs.description = "Description exceeds 4096 characters.";
    if ((form.content || "").length > 2000) errs.content = "Message content exceeds 2000 characters.";
    if ((form.footer || "").length > 2048) errs.footer = "Footer exceeds 2048 characters.";
    if ((form.author_name || "").length > 256) errs.author_name = "Author name exceeds 256 characters.";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const doSave = async () => {
    const { form } = modal;
    if (!validate(form)) return;
    setBusy("save");
    try {
      await saveEmbed(guildId, form.type.trim().toLowerCase(), {
        enabled: !!form.enabled,
        channel_id: form.channel_id || null,
        message: form.content || null,
        embed_color: form.embed_color || null,
        author_name: form.author_name || null,
        author_icon: form.author_icon?.trim() || null,
        thumbnail: form.thumbnail?.trim() || null,
        image: form.image?.trim() || null,
        footer: form.footer || null,
        mention_role: form.mention_role || null,
      });
      toast.success(`Template "${form.type.trim().toLowerCase()}" saved to Xevon.`);
      setModal(null);
      await refetch();
    } catch (e) {
      showApiError(e);
    } finally {
      setBusy(null);
    }
  };

  const doPreview = async () => {
    const { form } = modal;
    if (!validate(form)) return;
    setBusy("preview");
    try {
      await previewEmbed(guildId, { content: form.content || null, embed: toEmbedPayload(form) });
      toast.success("Xevon accepted the embed — it's valid for Discord.");
    } catch (e) {
      showApiError(e);
    }
    setBusy(null);
  };

  const doSend = async () => {
    const { form } = modal;
    if (!form.channel_id) {
      toast.error("Pick a channel to send to.");
      return;
    }
    if (!validate(form)) return;
    setBusy("send");
    try {
      const res = await sendEmbed(guildId, {
        channel_id: form.channel_id,
        content: form.content || null,
        embed: toEmbedPayload(form),
      });
      toast.success(
        <span>
          Embed sent.{" "}
          {res.message_url && (
            <a href={res.message_url} target="_blank" rel="noopener noreferrer" className="underline">
              View in Discord
            </a>
          )}
        </span>
      );
    } catch (e) {
      showApiError(e);
    } finally {
      setBusy(null);
    }
  };

  const remove = async (item) => {
    try {
      await deleteEmbed(guildId, item.type);
      toast.success("Template deleted.");
      await refetch();
    } catch (e) {
      showApiError(e);
    }
  };

  const setF = (patch) => setModal((m) => ({ ...m, form: { ...m.form, ...patch } }));

  return (
    <>
      <CrudCard
        icon={LayoutTemplate}
        title="Embed Builder"
        description="Design embeds, save templates to Xevon's database, and send them straight to a channel."
        testId="crud-embeds"
        id="embeds"
        loading={loading}
        error={error}
        onRetry={refetch}
        isEmpty={items.length === 0}
        emptyText="No saved embed templates yet — build one and hit Save."
        addLabel="New embed"
        onAdd={openNew}
      >
        <ul className="space-y-3">
          {items.map((item) => (
            <ListRow
              key={item.type}
              testId={`embed-row-${item.type}`}
              actions={
                <>
                  <button
                    onClick={() => setSendTarget(item)}
                    data-testid={`embed-send-${item.type}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-3.5 py-1.5 text-xs font-semibold text-paper-100 transition-colors duration-300 hover:bg-white hover:text-black"
                  >
                    <Send className="h-3 w-3" aria-hidden="true" />
                    Send
                  </button>
                  <IconButton onClick={() => openEdit(item)} label="Edit" testId={`embed-edit-${item.type}`}>
                    <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                  </IconButton>
                  <IconButton onClick={() => remove(item)} label="Delete" danger testId={`embed-delete-${item.type}`}>
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  </IconButton>
                </>
              }
            >
              <p className="font-mono text-sm font-semibold text-paper-100">{item.type}</p>
              <p className="mt-0.5 truncate text-xs text-paper-500">
                {item.message || "No content"} {item.channel_id && channelName(item.channel_id) ? `· ${channelName(item.channel_id)}` : ""}
                {item.enabled ? " · enabled" : " · disabled"}
              </p>
            </ListRow>
          ))}
        </ul>
      </CrudCard>

      {sendTarget && (
        <SendTemplateModal guildId={guildId} item={sendTarget} channels={channels} onClose={() => setSendTarget(null)} />
      )}

      <Modal open={!!modal} onClose={() => setModal(null)} title={modal?.isNew ? "New embed" : `Edit "${modal?.form.type}"`} testId="embed-modal">
        {modal && (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Template name" hint={fieldErrors.type}>
                <TextInput value={modal.form.type} onChange={(v) => setF({ type: v })} placeholder="announcement" testId="embed-form-type" />
              </Field>
              <Field label="Accent color">
                <ColorInput value={modal.form.embed_color} onChange={(v) => setF({ embed_color: v })} testId="embed-form-color" />
              </Field>
            </div>
            <Field label="Message content" hint={fieldErrors.content}>
              <textarea
                value={modal.form.content || ""}
                onChange={(e) => setF({ content: e.target.value })}
                rows={2}
                placeholder="Text above the embed…"
                className="w-full rounded-xl border border-white/10 bg-ink-800 px-4 py-2.5 text-sm text-paper-100 placeholder:text-paper-500/60 transition-colors duration-300 focus:border-white/30 focus:outline-none"
                data-testid="embed-form-message"
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Title" hint={fieldErrors.title}>
                <TextInput value={modal.form.title} onChange={(v) => setF({ title: v })} testId="embed-form-title" />
              </Field>
              <Field label="URL" hint={fieldErrors.url}>
                <TextInput value={modal.form.url} onChange={(v) => setF({ url: v })} placeholder="https://…" testId="embed-form-url" />
              </Field>
            </div>
            <Field label="Description" hint={fieldErrors.description}>
              <textarea
                value={modal.form.description || ""}
                onChange={(e) => setF({ description: e.target.value })}
                rows={3}
                className="w-full rounded-xl border border-white/10 bg-ink-800 px-4 py-2.5 text-sm text-paper-100 placeholder:text-paper-500/60 transition-colors duration-300 focus:border-white/30 focus:outline-none"
                data-testid="embed-form-description"
              />
            </Field>
            <ToggleRow label="Include timestamp" checked={modal.form.timestamp} onChange={(v) => setF({ timestamp: v })} testId="embed-form-timestamp" />

            <FieldsEditor fields={modal.form.fields} onChange={(fields) => setF({ fields })} />

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Author name" hint={fieldErrors.author_name}>
                <TextInput value={modal.form.author_name} onChange={(v) => setF({ author_name: v })} testId="embed-form-author" />
              </Field>
              <Field label="Author URL" hint={fieldErrors.author_url}>
                <TextInput value={modal.form.author_url} onChange={(v) => setF({ author_url: v })} placeholder="https://…" testId="embed-form-author-url" />
              </Field>
              <Field label="Author icon URL" hint={fieldErrors.author_icon}>
                <TextInput value={modal.form.author_icon} onChange={(v) => setF({ author_icon: v })} placeholder="https://…" testId="embed-form-author-icon" />
              </Field>
              <Field label="Thumbnail URL" hint={fieldErrors.thumbnail}>
                <TextInput value={modal.form.thumbnail} onChange={(v) => setF({ thumbnail: v })} placeholder="https://…" testId="embed-form-thumbnail" />
              </Field>
              <Field label="Image URL" hint={fieldErrors.image}>
                <TextInput value={modal.form.image} onChange={(v) => setF({ image: v })} placeholder="https://…" testId="embed-form-image" />
              </Field>
              <Field label="Footer text" hint={fieldErrors.footer}>
                <TextInput value={modal.form.footer} onChange={(v) => setF({ footer: v })} testId="embed-form-footer" />
              </Field>
              <Field label="Footer icon URL" hint={fieldErrors.footer_icon}>
                <TextInput value={modal.form.footer_icon} onChange={(v) => setF({ footer_icon: v })} placeholder="https://…" testId="embed-form-footer-icon" />
              </Field>
              <Field label="Mention role">
                <RoleSelect value={modal.form.mention_role} onChange={(v) => setF({ mention_role: v })} roles={selectableRoles} testId="embed-form-mention" />
              </Field>
            </div>

            <EmbedPreview form={modal.form} />

            <div className="space-y-3 rounded-xl border border-white/5 bg-white/[0.02] p-4">
              <Field label="Send to channel">
                <ChannelSelect value={modal.form.channel_id} onChange={(v) => setF({ channel_id: v })} channels={channels} types={["text", "news"]} testId="embed-form-channel" />
              </Field>
              <ChannelSendCheck guildId={guildId} channelId={modal.form.channel_id} />
            </div>

            <ToggleRow label="Template enabled" checked={modal.form.enabled} onChange={(v) => setF({ enabled: v })} testId="embed-form-enabled" />

            <div className="flex flex-col gap-2 sm:flex-row">
              <button
                onClick={doSend}
                disabled={!!busy}
                data-testid="embed-send-button"
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-white py-3 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.01] disabled:opacity-50"
              >
                <Send className="h-4 w-4" aria-hidden="true" />
                {busy === "send" ? "Sending…" : "Send Embed"}
              </button>
              <button
                onClick={doSave}
                disabled={!!busy}
                data-testid="embed-form-save"
                className="flex-1 rounded-full border border-white/20 py-3 text-sm font-semibold text-paper-100 transition-colors duration-300 hover:bg-white/10 disabled:opacity-50"
              >
                {busy === "save" ? "Saving…" : "Save Template"}
              </button>
              <button
                onClick={doPreview}
                disabled={!!busy}
                data-testid="embed-preview-button"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-5 py-3 text-sm font-semibold text-paper-100 transition-colors duration-300 hover:bg-white/10 disabled:opacity-50"
              >
                <Eye className="h-4 w-4" aria-hidden="true" />
                {busy === "preview" ? "Checking…" : "Validate"}
              </button>
            </div>
            <p className="text-xs text-paper-500/80">
              Send uses the full embed (title, fields, everything). Templates store the fields Xevon's database supports.
            </p>
          </div>
        )}
      </Modal>
    </>
  );
};
